import { CheckSquare, Calendar, Edit3, Hash, PenLine, Type } from 'lucide-react';
import type { DocumentSignFieldInput } from '@/lib/api';

type FieldType = DocumentSignFieldInput['type'];

interface FieldPaletteProps {
  selectedType: FieldType;
  onSelect: (type: FieldType) => void;
}

const TYPES: { type: FieldType; label: string; icon: typeof Edit3 }[] = [
  { type: 'signature', label: 'Signature', icon: PenLine },
  { type: 'initial', label: 'Initial', icon: Edit3 },
  { type: 'date_signed', label: 'Date signed', icon: Calendar },
  { type: 'signer_name', label: 'Signer name', icon: Hash },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

/**
 * Vertical rail of field types. Click a type to arm it; the next click on a
 * page in the editor places a field at the cursor. The currently-armed type
 * is highlighted and announced to assistive tech via `aria-pressed`.
 */
export function FieldPalette({ selectedType, onSelect }: FieldPaletteProps) {
  return (
    <ul role="toolbar" aria-label="Field types" className="space-y-1">
      {TYPES.map(({ type, label, icon: Icon }) => {
        const active = selectedType === type;
        return (
          <li key={type}>
            <button
              type="button"
              onClick={() => onSelect(type)}
              aria-pressed={active}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card hover:bg-accent border-transparent'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span>{label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export type { FieldType };
